import type { ReactNode } from "react";
import { Pencil } from "lucide-react";
import { briefSubject } from "@shared/commission";
import { useIntakeCopy } from "../i18n/useIntakeCopy";
import { BLOCK_KEYS, type BlockKey, type IntakeDraft, type StepId } from "../types";

export interface RecapBlock { text?: string; refs: string[] }
export interface RecapCharacter { type: string; name?: string; look: RecapBlock; action: RecapBlock; extra: RecapBlock }
export interface RecapData {
  types: string[]; backgroundType: string | null; usageType: string | null;
  contentRating: string | null; deadline: string | null; postingPermission: string | null;
  characters: RecapCharacter[];
  scene?: { description?: string; refs: string[] }; notes?: string; contact?: string;
}

const block = (b: { text: string; refs: string[] }): RecapBlock => ({ text: b.text.trim() || undefined, refs: b.refs });

export function recapFromDraft(d: IntakeDraft): RecapData {
  const chars = d.characters.slice(0, d.characterCount);
  return {
    types: chars.map((c) => c.type ?? ""), backgroundType: d.backgroundType,
    usageType: d.usageType, contentRating: d.contentRating, deadline: d.deadlineMode === "date" ? d.deadline : null,
    postingPermission: d.postingPermission,
    characters: chars.map((c) => ({ type: c.type ?? "", name: c.name.trim() || undefined, look: block(c.look), action: block(c.action), extra: block(c.extra) })),
    scene: d.backgroundType === "complex" ? { description: d.sceneDescription || undefined, refs: d.sceneRefs } : undefined,
    notes: d.notes || undefined,
    contact: [d.clientName, d.clientEmail, d.clientDiscord].filter(Boolean).join(" · "),
  };
}

const thumb = (u: string) => u.replace("/upload/", "/upload/c_fill,w_96,h_96,q_auto,f_auto/");

function Block({ title, step, onEdit, children }: { title: string; step: StepId; onEdit?: (s: StepId) => void; children: ReactNode }) {
  const { c } = useIntakeCopy();
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-medium text-[var(--color-text-muted)]">{title}</h4>
        {onEdit && <button type="button" onClick={() => onEdit(step)} className="flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"><Pencil className="h-3 w-3" />{c.review.edit}</button>}
      </div>
      {children}
    </div>
  );
}

export function Recap({ data, onEdit }: { data: RecapData; onEdit?: (step: StepId) => void }) {
  const { c, fmt } = useIntakeCopy();
  const r = c.review.recap;
  const bg = data.backgroundType as "none" | "simple" | "complex" | null;
  const use = data.usageType as keyof typeof c.scene.use | null;
  const rating = data.contentRating as keyof typeof c.scene.ratings | null;
  const post = data.postingPermission as keyof typeof c.contact.post | null;
  const blockTitle = (k: BlockKey) => c.characters.blocks[k].title;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Block title={r.type} step="type" onEdit={onEdit}>
        <p className="text-[var(--color-text)]">{briefSubject(data.types.filter(Boolean))}</p>
      </Block>
      <Block title={c.steps.scene} step="scene" onEdit={onEdit}>
        <p className="text-sm text-[var(--color-text)]">
          {[bg && c.scene.bg[bg].title, use && c.scene.use[use], rating && c.scene.ratings[rating], data.deadline ?? c.scene.noRush].filter(Boolean).join(" · ")}
        </p>
      </Block>
      <div className="sm:col-span-2">
        <Block title={r.characters} step="characters" onEdit={onEdit}>
          <ul className="space-y-4">
            {data.characters.map((ch, i) => (
              <li key={i} className="space-y-2 text-sm">
                <p className="font-medium text-[var(--color-text)]">{[ch.name || fmt(c.characters.card, { n: i + 1 }), ch.type].filter(Boolean).join(" · ")}</p>
                {BLOCK_KEYS.filter((k) => ch[k].text || ch[k].refs.length > 0).map((k) => (
                  <div key={k}>
                    <p className="text-xs uppercase tracking-wide text-[var(--color-text-muted)]">{blockTitle(k)}</p>
                    {ch[k].text && <p className="whitespace-pre-wrap text-[var(--color-text)]">{ch[k].text}</p>}
                    {ch[k].refs.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {ch[k].refs.map((u) => <img key={u} src={thumb(u)} alt="" className="h-12 w-12 rounded-md object-cover" />)}
                      </div>
                    )}
                  </div>
                ))}
              </li>
            ))}
          </ul>
          {data.scene && <p className="mt-3 text-sm text-[var(--color-text-muted)]">{r.scene}{data.scene.description ? `: ${data.scene.description}` : ""} · {fmt(r.refs, { n: data.scene.refs.length })}</p>}
          {data.notes && <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-text-muted)]">{r.notes}: {data.notes}</p>}
        </Block>
      </div>
      {data.contact !== undefined && (
        <div className="sm:col-span-2">
          <Block title={r.contact} step="contact" onEdit={onEdit}>
            <p className="text-sm text-[var(--color-text)]">{data.contact}{post ? ` · ${c.contact.post[post]}` : ""}</p>
          </Block>
        </div>
      )}
    </div>
  );
}
